import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Clock, MapPin, CalendarDays, FileText, Hand, Activity, Brain } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ICONS = { Hand, Activity, Brain };

const STATE_LABEL = {
    completed: { label: "Complétée", color: "bg-brand-orange text-white" },
    upcoming: { label: "À venir", color: "bg-brand-orange/15 text-brand-orange" },
    not_enrolled: { label: "Non inscrit", color: "bg-brand-line text-brand-ink/60" },
    cancelled: { label: "Annulée", color: "bg-red-100 text-red-700" },
};

function formatDate(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

export default function StagiaireCursusDetail() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/stagiaire/cursus/${slug}`)
            .then((r) => setData(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) return <div className="p-10 text-brand-smoke">Chargement…</div>;
    if (!data) return <div className="p-10">Cursus introuvable.</div>;

    const { cursus, formations } = data;
    const Icon = ICONS[cursus.icon] || Hand;
    const completed = formations.filter((f) => f.state === "completed").length;
    const total = formations.length;
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

    return (
        <div className="mx-auto max-w-6xl px-6 py-10" data-testid="page-stagiaire-cursus-detail">
            <Link to="/stagiaire/cursus" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-smoke hover:text-brand-orange">
                <ArrowLeft className="h-3.5 w-3.5" /> Mes cursus
            </Link>

            {/* Hero */}
            <div className="relative mt-6 overflow-hidden rounded-3xl shadow-premium-lg">
                <img src={cursus.image_url} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-brand-ink via-brand-ink/85 to-brand-ink/40" />
                <div className="relative p-8 text-white md:p-12">
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/[0.06] px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] backdrop-blur">
                        <Icon className="h-3.5 w-3.5 text-brand-orange" />
                        Axe de prévention
                    </div>
                    <h1 className="mt-6 font-display text-4xl font-normal leading-[1] tracking-tightest md:text-6xl">
                        {cursus.name.replace("Cursus ", "")}
                    </h1>
                    <p className="mt-3 max-w-2xl text-base italic text-brand-orange">{cursus.tagline}</p>

                    <div className="mt-8 flex flex-wrap items-center gap-6">
                        <div>
                            <div className="font-display text-5xl text-brand-orange">{pct}%</div>
                            <div className="mt-1 text-[11px] uppercase tracking-wider text-white/60">progression</div>
                        </div>
                        <div className="h-12 w-px bg-white/15" />
                        <div className="flex-1 max-w-md">
                            <div className="grid grid-cols-3 gap-1.5">
                                {formations.map((f) => {
                                    let cls = "h-2 rounded-full ";
                                    if (f.state === "completed") cls += "bg-brand-orange";
                                    else if (f.state === "upcoming") cls += "bg-brand-orange/40";
                                    else cls += "bg-white/15";
                                    return <div key={f.slug} className={cls} />;
                                })}
                            </div>
                            <div className="mt-2 text-xs text-white/65">
                                {completed}/{total} formations complétées
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Timeline */}
            <h2 className="mt-12 font-display text-3xl">Les 3 formations du cursus</h2>
            <ol className="mt-8 space-y-6">
                {formations.map((f, idx) => {
                    const state = STATE_LABEL[f.state] || STATE_LABEL.not_enrolled;
                    const lastEnroll = (f.enrollments || [])[0];
                    return (
                        <li key={f.slug} className="relative flex gap-6" data-testid={`cursus-formation-${f.slug}`}>
                            <div className="relative flex flex-col items-center">
                                <div className={`flex h-12 w-12 items-center justify-center rounded-full font-display ${
                                    f.state === "completed" ? "bg-brand-orange text-white" :
                                    f.state === "upcoming" ? "bg-white text-brand-orange ring-[3px] ring-brand-orange" :
                                    "bg-brand-line text-brand-smoke"
                                }`}>
                                    {f.state === "completed" ? <CheckCircle2 className="h-5 w-5" /> : <span>{String(idx + 1).padStart(2, "0")}</span>}
                                </div>
                                {idx < formations.length - 1 && <div className="mt-2 h-full w-px bg-brand-line" />}
                            </div>

                            <div className="flex-1 rounded-2xl border border-brand-line bg-white p-6 shadow-premium">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <h3 className="font-display text-2xl text-brand-ink">{f.name}</h3>
                                        <p className="mt-2 max-w-xl text-sm text-brand-ink/70">{f.short_description}</p>
                                    </div>
                                    <span className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wider ${state.color}`}>
                                        {state.label}
                                    </span>
                                </div>

                                {lastEnroll && (
                                    <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-brand-ink/70">
                                        <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3 w-3 text-brand-orange" /> {formatDate(lastEnroll.session.start_date)}</span>
                                        <span className="inline-flex items-center gap-1.5"><MapPin className="h-3 w-3 text-brand-orange" /> {lastEnroll.session.city}</span>
                                        <span className="inline-flex items-center gap-1.5"><Clock className="h-3 w-3 text-brand-orange" /> {lastEnroll.session.start_time}–{lastEnroll.session.end_time}</span>
                                    </div>
                                )}

                                <div className="mt-6 flex flex-wrap gap-3">
                                    {lastEnroll && (
                                        <Link
                                            to={`/stagiaire/sessions/${lastEnroll.session.id}`}
                                            className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-4 py-2 text-xs font-semibold text-white hover:bg-brand-coal"
                                            data-testid={`cursus-formation-cta-${f.slug}`}
                                        >
                                            <FileText className="h-3.5 w-3.5" />
                                            {f.state === "completed" ? "Voir mes documents" : "Voir la session"}
                                        </Link>
                                    )}
                                    {!lastEnroll && (
                                        <Link
                                            to={`/sessions?cursus=${cursus.slug}`}
                                            className="inline-flex items-center gap-2 rounded-full border border-brand-line px-4 py-2 text-xs font-semibold hover:border-brand-orange hover:text-brand-orange"
                                        >
                                            Voir les sessions disponibles <ArrowRight className="h-3.5 w-3.5" />
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ol>

            <div className="mt-12 rounded-3xl bg-brand-ink p-10 text-center text-white">
                <div className="text-xs uppercase tracking-[0.25em] text-brand-orange">Continuez votre parcours</div>
                <h3 className="mt-4 font-display text-3xl">Continuez votre parcours <em className="italic text-brand-orange">Prévention Active</em>.</h3>
                <Link to="/sessions" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white">
                    Voir les sessions disponibles <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
        </div>
    );
}
